import { styledPolymorphic } from '@qred/components-library';

export const MainContainer = styledPolymorphic<
  'div',
  {
    $background?: React.CSSProperties['background'];
    $backgroundOpacity?: React.CSSProperties['opacity'];
    $backgroundColor?: React.CSSProperties['backgroundColor'];
    $isInIframe?: boolean;
  }
>()(
  (styled) => styled`
  display: flex;
  flex-direction: column;
  ${({ $isInIframe }) =>
    $isInIframe
      ? `
      height: 100%;
      `
      : `
      min-height: var(--client-height);
      `}
  width: 100%;
  position: relative;
  font-size: 16px;

  ${({ $backgroundColor, $isInIframe }) => {
    if ($backgroundColor && !$isInIframe) {
      return `
        background-color: ${$backgroundColor};
      `;
    }
    return '';
  }}

  ${({ $background, $backgroundOpacity }) => {
    if ($background && $backgroundOpacity) {
      return `
        &::before {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1;
          background: ${$background};
          opacity: ${$backgroundOpacity};
        }
      `;
    } else if ($background) {
      return `
        background: ${$background};
      `;
    }
    return '';
  }}

  & > div {
    position: relative;
    z-index: 2;
  }
`
);

export const StepsContainer = styledPolymorphic<
  'div',
  {
    $justify?: React.CSSProperties['justifyContent'];
    $isInIframe?: boolean;
  }
>()(
  (styled) => styled`
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => props.$justify ?? 'flex-start'};
  align-items: center;
  align-self: center;
  width: 100%;
  padding: 0;
  height: ${(props) =>
    props.$isInIframe
      ? 'unset'
      : `calc(var(--client-height) - (var(--navbar-height) * 2))`}; // total screen height - 2x navbar height

  ${({ theme, $isInIframe }) =>
    `${theme.fn.largerThan('tablet')} {
        max-width: ${$isInIframe ? '100%' : '40em'};
        height: ${
          $isInIframe
            ? '100%'
            : `calc(var(--client-height) - (var(--navbar-height) * 4))`
        }; // total screen height - 4x navbar height
        margin-top: 18px;
      }
    `}
`
);

export const StandardStepContentFooter = styledPolymorphic<
  'div',
  { $isInIframe?: boolean }
>()(
  (styled) => styled`
  ${({ theme, $isInIframe }) => `
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  border-top: 1px solid ${theme.fn.themeColor('Main/Grey/Alpha/OnLight/10%')};
  padding: ${$isInIframe ? '24px 32px' : '16px'};
  background-color: ${theme.fn.themeColor('Container/Option_2_(White)')};
  text-align: center;
    
  ${theme.fn.largerThan('tablet')} {
    padding: 24px 32px;
    background-color: ${theme.fn.themeColor('Container/Option_4_(Grey)')};
  }

  button {
    align-self: center;
    }
 `}
`
);

export const StandardPageContainer = styledPolymorphic<
  'div',
  { $background?: string; $isInIframe?: boolean }
>()(
  (styled) => styled`
  ${({ theme, $background, $isInIframe }) => `
    align-items: center;
    display: flex;
    flex-direction: column;
    flex: 1;
    height: var(--client-height);
    background-color: ${
      $isInIframe ? 'transparent' : theme.fn.themeColor('Page/Option_1_(White)')
    };
    ${theme.fn.largerThan('tablet')} {
      background-color: ${
        $isInIframe
          ? 'transparent'
          : theme.fn.themeColor('Page/Option_2_(Green)')
      };
    }

    ${$background && `background: ${$background} !important;`}
  `}
`
);

export const StandardStepContent = styledPolymorphic<'div', {}>()(
  (_styled) => _styled`
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex: 1;
  width: 100%;
  
  ${({ theme }) =>
    `
  background-color: ${theme.fn.themeColor('Page/Option_1_(White)')};
  ${theme.fn.largerThan('tablet')} {
      width: 40em; 
      margin-bottom: 60px;
    }
  `}
`
);

export const StandardStepContentHeader = styledPolymorphic<'div', {}>()(
  (styled) => styled`
    padding: 24px 16px;

  ${({ theme }) =>
    `
    ${theme.fn.largerThan('tablet')} {
        padding: 32px;
      }
  `}
  `
);

export const StandardStepContentBody = styledPolymorphic<
  'div',
  { $isInIframe?: boolean }
>()(
  (styled) => styled`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  overflow: auto;
  padding: ${({ $isInIframe }) => ($isInIframe ? '24px 32px' : '24px 16px')};

  ${({ theme }) =>
    `
    ${theme.fn.largerThan('tablet')} {
        padding: 32px;
      }
  `}
`
);

export const GoldenRatioContainer = styledPolymorphic<
  'div',
  { $grow?: boolean }
>()(
  (styled) => styled`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  &::before {
    content: '';
    flex: 0.382; /* Golden ratio - creates space above (38.2%) */
  }

  ${({ $grow }) =>
    $grow
      ? `
        > :last-of-type {
          flex: 1; /* Allows the last child to grow and fill the remaining space */
        }
      `
      : `
      &::after {
        content: '';
        flex: 0.618; /* Creates space below (61.8%) */
      }
      `}
    `
);
